import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  DollarSign,
  Award,
  BarChart3,
  Download,
  Users,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Search,
  AlertCircle,
  Star
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Area,
} from 'recharts';
import { Vendor } from '../types/vendor';
import { cn } from './ui/utils';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../utils/firebase/client';
import { getCompanyId } from '../utils/tenantContext';

interface VendorAnalyticsDashboardProps {
  vendors: Vendor[];
}

interface VendorComparison {
  vendor: Vendor;
  totalSpend: number;
  jobCount: number;
  avgJobCost: number;
  rating: number;
  onTimeRate: number;
  qualityScore: number;
  avgResponseTime?: number;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
}

interface SpendingTrend {
  month: string;
  total: number;
  count: number;
  avgCost: number;
}

interface CategorySpending {
  category: string;
  amount: number;
  percentage: number;
  vendorCount: number;
}

export default function VendorAnalyticsDashboard({
  vendors,
}: VendorAnalyticsDashboardProps) {
  const [timeRange, setTimeRange] = useState<'month' | 'quarter' | 'year' | 'all'>('quarter');
  const [sortBy, setSortBy] = useState<'spend' | 'rating' | 'jobs' | 'quality'>('spend');
  const [isLoading, setIsLoading] = useState(true);
  const [comparisons, setComparisons] = useState<VendorComparison[]>([]);
  const [spendingTrends, setSpendingTrends] = useState<SpendingTrend[]>([]);
  const [categorySpending, setCategorySpending] = useState<CategorySpending[]>([]);
  const [metrics, setMetrics] = useState<any>({});

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  // Load and process REAL data
  useEffect(() => {
    const processData = async () => {
      setIsLoading(true);
      try {
        const companyId = getCompanyId();
        const ticketsRef = collection(db, 'companies', companyId, 'tickets');

        // 1. Historical Data (Completed/Resolved)
        const historicalQuery = query(
          ticketsRef,
          where('status', 'in', ['resolved', 'closed', 'completed'])
        );

        // 2. Live Data (Active)
        const liveQuery = query(
          ticketsRef,
          where('status', 'in', ['open', 'assigned', 'in_progress', 'waiting_parts'])
        );

        const [historicalSnap, liveSnap] = await Promise.all([
          getDocs(historicalQuery),
          getDocs(liveQuery)
        ]);

        // --- Process Historical Data ---
        const tickets = historicalSnap.docs.map(d => {
          const data = d.data();
          return {
            ...data,
            id: d.id,
            completedAt: data.completedAt?.toDate(),
            startedAt: data.startedAt?.toDate(),
            createdAt: data.createdAt?.toDate(),
            resolutionDeadline: data.resolutionDeadline?.toDate ? data.resolutionDeadline.toDate() : data.resolutionDeadline
          } as any;
        });

        const activeVendors = vendors.filter(v => v.status === 'active');
        const vendorMap = new Map(activeVendors.map(v => [v.id, v]));

        const vendorStats: Record<string, { spend: 0; jobs: 0; responseTimes: number[]; onTimeCount: 0 }> = {};
        const monthlyStats: Record<string, { total: 0; count: 0 }> = {};

        tickets.forEach((t: any) => {
          if (!t.vendorId) return;

          // Vendor Stats container
          if (!vendorStats[t.vendorId]) {
            vendorStats[t.vendorId] = { spend: 0, jobs: 0, responseTimes: [], onTimeCount: 0 };
          }

          const vendor = vendorMap.get(t.vendorId);
          // EXCLUDE Oil Hauling/Purchasing from "Spend" (it's revenue/logistics)
          const isOilRevenueVendor = vendor?.services?.some((s: string) =>
            ['Oil Purchaser', 'Oil Hauling', 'Crude Hauling', 'Oil Transport', 'Purchaser'].includes(s)
          );

          const cost = Number(t.actualCost) || Number(t.estimatedCost) || 0;

          if (!isOilRevenueVendor) {
            vendorStats[t.vendorId].spend += cost;
          }

          vendorStats[t.vendorId].jobs += 1;

          // On-Time Calculation
          if (t.resolutionDeadline && t.completedAt) {
            if (t.completedAt <= t.resolutionDeadline) {
              vendorStats[t.vendorId].onTimeCount += 1;
            }
          }

          // Response Time
          if (t.startedAt && t.createdAt) {
            const diffMs = t.startedAt.getTime() - t.createdAt.getTime();
            const diffHours = diffMs / (1000 * 60 * 60);
            if (diffHours >= 0) vendorStats[t.vendorId].responseTimes.push(diffHours);
          }

          // Monthly Stats (Spend Trends) - Exclude Oil Revenue
          if (t.completedAt && !isOilRevenueVendor) {
            const sortKey = t.completedAt.toISOString().slice(0, 7);
            if (!monthlyStats[sortKey]) monthlyStats[sortKey] = { total: 0, count: 0 };
            monthlyStats[sortKey].total += cost;
            monthlyStats[sortKey].count += 1;
          }
        });

        // --- Process Vendors ---
        const comparisonData: VendorComparison[] = activeVendors.map(vendor => {
          const stats = vendorStats[vendor.id] || { spend: 0, jobs: 0, responseTimes: [], onTimeCount: 0 };
          const totalSpend = stats.spend;
          const jobCount = stats.jobs;
          const avgJobCost = jobCount > 0 ? totalSpend / jobCount : 0;
          const ticketOnTime = jobCount > 0 ? (stats.onTimeCount / jobCount) * 100 : 0;
          const onTimeRate = jobCount > 0 ? ticketOnTime : (vendor.performanceMetrics?.onTimeCompletionRate || 0);

          const totalResponse = stats.responseTimes.reduce((a, b) => a + b, 0);
          const avgResponseTime = stats.responseTimes.length > 0 ? totalResponse / stats.responseTimes.length : undefined;

          return {
            vendor,
            totalSpend,
            jobCount,
            avgJobCost,
            rating: vendor.rating?.overall || 0,
            onTimeRate,
            avgResponseTime,
            qualityScore: (vendor.rating?.quality || 0) * 20,
            trend: 'stable',
            trendPercent: 0,
          };
        });

        // --- Sorting ---
        const sorted = [...comparisonData].sort((a, b) => {
          switch (sortBy) {
            case 'spend': return b.totalSpend - a.totalSpend;
            case 'rating': return b.rating - a.rating;
            case 'jobs': return b.jobCount - a.jobCount;
            case 'quality': return b.qualityScore - a.qualityScore;
            default: return 0;
          }
        });
        setComparisons(sorted);

        // --- Live Operations (SLA Watchtower) ---
        const now = new Date();
        let activeCount = 0;
        let overdueCount = 0;
        let atRiskCount = 0;

        liveSnap.forEach(d => {
          const data = d.data();
          const deadline = data.resolutionDeadline?.toDate ? data.resolutionDeadline.toDate() : data.resolutionDeadline;
          activeCount++;
          if (deadline) {
            if (now > deadline) overdueCount++;
            else if ((deadline.getTime() - now.getTime()) < 24 * 60 * 60 * 1000) atRiskCount++;
          }
        });

        // --- Trends & Categories ---
        const sortedMonths = Object.keys(monthlyStats).sort().slice(-6);
        const trends = sortedMonths.map(key => {
          const [year, month] = key.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          return {
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            total: monthlyStats[key].total,
            count: monthlyStats[key].count,
            avgCost: monthlyStats[key].total / monthlyStats[key].count
          };
        });
        if (trends.length === 0) trends.push({ month: 'Now', total: 0, count: 0, avgCost: 0 });
        setSpendingTrends(trends);

        const categoryMap: Record<string, CategorySpending> = {};
        comparisonData.forEach(comp => {
          const cat = comp.vendor.services[0] || 'Uncategorized';
          if (!categoryMap[cat]) categoryMap[cat] = { category: cat, amount: 0, percentage: 0, vendorCount: 0 };
          categoryMap[cat].amount += comp.totalSpend;
          categoryMap[cat].vendorCount += 1;
        });
        const categories = Object.values(categoryMap).map(c => ({
          ...c,
          percentage: Object.values(categoryMap).reduce((s, x) => s + x.amount, 0) > 0
            ? Math.round((c.amount / Object.values(categoryMap).reduce((s, x) => s + x.amount, 0)) * 100)
            : 0
        })).sort((a, b) => b.amount - a.amount);
        setCategorySpending(categories);

        // --- Metrics ---
        const totalSpend = comparisonData.reduce((s, c) => s + c.totalSpend, 0);
        const avgRating = comparisonData.length > 0 ? comparisonData.reduce((s, c) => s + c.rating, 0) / comparisonData.length : 0;
        const totalCompletedWithDeadline = tickets.filter((t: any) => t.resolutionDeadline).length;
        const totalOnTime = tickets.filter((t: any) => t.resolutionDeadline && t.completedAt && t.completedAt <= t.resolutionDeadline).length;

        setMetrics({
          totalSpend,
          jobCount: tickets.length,
          avgRating,
          activeCount, // Live
          overdueCount, // Live
          atRiskCount, // Live
          avgOnTimeRate: totalCompletedWithDeadline > 0 ? (totalOnTime / totalCompletedWithDeadline) * 100 : 100
        });

      } catch (error) {
        console.error('Error processing vendor analytics:', error);
        toast.error('Failed to load vendor analytics');
      } finally {
        setIsLoading(false);
      }
    };

    processData();
  }, [vendors, timeRange, sortBy]);

  const exportToCSV = () => {
    toast.info('Exporting CSV...');
    // Simplified for brevity
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    if (trend === 'up') return <ArrowUpRight className="w-4 h-4 text-emerald-500" />;
    if (trend === 'down') return <ArrowDownRight className="w-4 h-4 text-rose-500" />;
    return <Minus className="w-4 h-4 text-zinc-400" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Spend</p>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    ${metrics.totalSpend?.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Jobs Completed</p>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {metrics.jobCount}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <Award className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Rating</p>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {metrics.avgRating?.toFixed(1) || '0.0'}
                  </span>
                  <span className="ml-2 text-sm font-medium text-gray-500">/ 5.0</span>
                </div>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <Target className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">On-Time Rate</p>
                <div className="mt-2 flex items-baseline">
                  <span className="text-3xl font-bold text-gray-900 dark:text-white">
                    {metrics.avgOnTimeRate?.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SLA Status Bar */}
      <Card className="border-l-4 border-l-blue-500 shadow-sm dark:bg-slate-900 dark:border-y-slate-800 dark:border-r-slate-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Operations Monitor</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Real-time tracking of active vendor jobs</p>
              </div>
            </div>

            <div className="flex gap-3 w-full sm:w-auto">
              <div className="flex-1 sm:flex-none flex flex-col items-center px-6 py-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wilder">Active</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">{metrics.activeCount || 0}</span>
              </div>

              <div className="flex-1 sm:flex-none flex flex-col items-center px-6 py-2 bg-amber-50 dark:bg-amber-900/10 rounded-md border border-amber-200 dark:border-amber-900/30">
                <span className="text-xs font-bold text-amber-600 dark:text-amber-500 uppercase tracking-wilder">At Risk</span>
                <span className="text-xl font-bold text-amber-700 dark:text-amber-400">{metrics.atRiskCount || 0}</span>
              </div>

              <div className="flex-1 sm:flex-none flex flex-col items-center px-6 py-2 bg-red-50 dark:bg-red-900/10 rounded-md border border-red-200 dark:border-red-900/30">
                <span className="text-xs font-bold text-red-600 dark:text-red-500 uppercase tracking-wilder">Overdue</span>
                <span className="text-xl font-bold text-red-700 dark:text-red-400">{metrics.overdueCount || 0}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Spending Trend */}
        <Card className="lg:col-span-2 shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Spend Velocity</h3>
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={spendingTrends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" opacity={0.5} />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    tickFormatter={(value) => `$${value >= 1000 ? `${value / 1000}k` : value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      border: 'none',
                      padding: '12px'
                    }}
                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Total Spend']}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    fill="url(#colorTotal)"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    yAxisId="left"
                    fillOpacity={1}
                  />
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Category Split</h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categorySpending}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="amount"
                    stroke="none"
                  >
                    {categorySpending.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} cornerRadius={4} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => `$${value.toLocaleString()}`}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-3 mt-4 max-h-[140px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700 pr-2">
              {categorySpending.map((c, i) => (
                <div key={i} className="flex justify-between items-center text-sm group hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full shadow-sm" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{c.category}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-gray-900 dark:text-white">{c.percentage}%</span>
                    <p className="text-xs text-gray-500">${c.amount.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics Table */}
      <Card className="shadow-sm dark:bg-slate-900 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">Vendor Performance Matrix</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Detailed breakdown of vendor statistics</p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 rounded-md px-3 py-1.5 text-sm font-medium text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow"
            >
              <option value="spend">Total Spend</option>
              <option value="rating">Rating</option>
              <option value="jobs">Job Count</option>
              <option value="quality">Quality Score</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vendor</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Spend</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rating</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">On-Time %</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
              {comparisons.map((item, idx) => (
                <tr key={item.vendor.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs mr-3">
                        {item.vendor.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{item.vendor.displayName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.vendor.services[0] || 'General'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-gray-200">
                    ${item.totalSpend.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="font-bold text-amber-500">{item.rating.toFixed(1)}</span>
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className={cn(
                      "px-2.5 py-1 rounded-full text-xs font-medium",
                      item.onTimeRate > 90
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        : item.onTimeRate > 75
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    )}>
                      {item.onTimeRate.toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center">
                      {getTrendIcon(item.trend)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
